import {
  formatCityLine,
  formatNip,
  INVOICE_SOURCE_LABELS,
  type Invoice,
  type InvoiceDetail,
} from '@faktorplus/shared';
import { useCompany } from '../../api/company';
import { formatAmount, formatDate } from '../../lib/format';
import styles from './invoice-document.module.css';

const quantityFormatter = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

/**
 * The invoice as a document, rendered from the database.
 *
 * A scan is evidence, not the record: KSeF delivers structured data with no
 * PDF at all, and a hand-keyed paper invoice may never get a file. Everything
 * needed to answer "who billed us, for what, how much" has to come from here.
 */
/**
 * Us, on the other side of the document.
 *
 * Read from the API rather than baked into the bundle, so that the buyer here
 * and the invoice details on an outgoing order are the same company — they are
 * the same environment variables.
 */
function Buyer() {
  const { data: company } = useCompany();

  return (
    <section>
      <p className={styles.partyLabel}>Nabywca</p>
      {company && (
        <>
          <p className={styles.partyName}>{company.name}</p>
          <p className={styles.partyLine}>{company.street}</p>
          <p className={styles.partyLine}>{formatCityLine(company)}</p>
          <p className={styles.partyNip}>NIP: {formatNip(company.nip)}</p>
        </>
      )}
    </section>
  );
}

export function InvoiceDocument({ invoice }: { invoice: Invoice | InvoiceDetail }) {
  const hasRetention = invoice.retentionAmount > 0;

  return (
    <article className={styles.document}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Faktura VAT</h2>
          <p className={styles.number}>{invoice.number}</p>
        </div>
        <span
          className={`${styles.source} ${invoice.source === 'KSEF' ? styles.sourceKsef : ''}`}
        >
          {INVOICE_SOURCE_LABELS[invoice.source]}
        </span>
      </header>

      <div className={styles.parties}>
        <section>
          <p className={styles.partyLabel}>Sprzedawca</p>
          <p className={styles.partyName}>{invoice.vendorName}</p>
          {invoice.vendorAddress && <p className={styles.partyLine}>{invoice.vendorAddress}</p>}
          <p className={styles.partyNip}>NIP: {formatNip(invoice.vendorNip)}</p>
        </section>

        <Buyer />
      </div>

      <div className={styles.dates}>
        <Fact label="Data wystawienia" value={formatDate(invoice.issueDate)} />
        <Fact label="Data wpływu" value={formatDate(invoice.receivedDate)} />
        <Fact label="Termin płatności" value={formatDueDate(invoice)} />
        {invoice.paidAt && <Fact label="Zapłacono" value={formatDate(invoice.paidAt)} />}
      </div>

      <LineItems invoice={invoice} />

      <div className={styles.totals}>
        <div className={styles.totalRow}>
          <span>Razem netto</span>
          <span>{formatAmount(invoice.netAmount)}</span>
        </div>
        <div className={styles.totalRow}>
          <span>VAT</span>
          <span>{formatAmount(invoice.vatAmount)}</span>
        </div>
        <div className={`${styles.totalRow} ${styles.totalGrand}`}>
          <span>Razem brutto</span>
          <span>{formatAmount(invoice.grossAmount)} zł</span>
        </div>

        {hasRetention && (
          <>
            <div className={`${styles.totalRow} ${styles.totalRetention}`}>
              <span>Zatrzymanie (kaucja)</span>
              <span>−{formatAmount(invoice.retentionAmount)}</span>
            </div>
            <div className={`${styles.totalRow} ${styles.totalGrand}`}>
              <span>Do zapłaty</span>
              <span>{formatAmount(invoice.amountDue)} zł</span>
            </div>
          </>
        )}
      </div>

      {invoice.notes && <p className={styles.notes}>{invoice.notes}</p>}
    </article>
  );
}

function LineItems({ invoice }: { invoice: Invoice | InvoiceDetail }) {
  if (invoice.lineItems.length === 0) {
    return (
      <p className={styles.noItems}>
        Pozycje nie zostały wprowadzone — faktura jest rozliczana kwotą zbiorczą.
        {invoice.attachments.length > 0 && ' Szczegóły znajdziesz na skanie.'}
      </p>
    );
  }

  return (
    <div className={styles.itemsWrap}>
      <table className={styles.items}>
        <thead>
          <tr>
            <th className={styles.itemIndex}>Lp.</th>
            <th>Nazwa towaru / usługi</th>
            <th>J.m.</th>
            <th className={styles.numeric}>Ilość</th>
            <th className={styles.numeric}>Cena netto</th>
            <th className={styles.numeric}>VAT</th>
            <th className={styles.numeric}>Wartość netto</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lineItems.map((item) => (
            <tr key={item.id}>
              <td className={styles.itemIndex}>{item.position}</td>
              <td className={styles.itemName}>{item.name}</td>
              <td>{item.unit ?? '—'}</td>
              <td className={styles.numeric}>{quantityFormatter.format(item.quantity)}</td>
              <td className={styles.numeric}>{formatAmount(item.unitPrice)}</td>
              <td className={styles.numeric}>{item.vatRate}%</td>
              <td className={styles.numeric}>{formatAmount(item.netAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className={styles.dateLabel}>{label}</p>
      <p className={styles.dateValue}>{value}</p>
    </div>
  );
}

/**
 * An invoice still waiting for its handover protocol has no running term, so
 * printing a date would be fiction — say what it is actually waiting for.
 */
function formatDueDate(invoice: Invoice | InvoiceDetail): string {
  if (invoice.requiresHandoverProtocol && !invoice.documentsCompleteAt) {
    return 'czeka na protokół';
  }
  return formatDate(invoice.dueDate);
}
